import Dialog from '@ixigo/iui/Dialog';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useState } from 'react';
import { getServices } from '../../apis/service';
import { Event } from '../../enums/event';
import useNavigateWithTransition from '../../hooks/useNavigateWithTransition';
import { recordEvent } from '../../utils/analytics';
import { isMobile } from '../../utils/common';
import DesktopSlide from '../DesktopSlide';
import DialogHeader from '../DialogHeader';
import ErrorSection from '../Error/Section';
import ServiceDetail from '../ServiceDetail';
import MobServiceList from './MobList';
import Skeleton from './Skeleton';
import WebServiceList from './WebList';
import { Service } from './index.types';

const ServiceList: FC = () => {
    const navigate = useNavigateWithTransition();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState<Service[]>([]);
    const [selection, setSelection] = useState<{ title?: string; id?: string } | null>(null);
    const isMob = isMobile();

    const fetchServices = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getServices();
            setData(response);
            setError(false);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const onServiceCardClick = useCallback(
        (service: Service) => {
            const linkArr = service.action.link.split('/');
            const link = linkArr[linkArr.length - 1];

            recordEvent(Event.HC_SERVICE_OPEN, {
                additionalParams: {
                    serviceName: service.header,
                    serviceId: service._id,
                },
            });

            if (!isMob) {
                setSelection({
                    title: service.header,
                    id: link,
                });
                return;
            }

            navigate(`faqs/${link}`, {
                state: { headerText: service.header },
                type: 'forward',
            });
        },
        [isMob, navigate],
    );

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    if (loading) return <Skeleton />;

    if (error) return <ErrorSection sectionName='quick guide' onClickRetry={fetchServices} />;

    if (!data.length) return null;

    return (
        <div
            className={clsx(
                'bg-white',
                isMob ? 'gap-10 p-20 pb-10' : 'gap-20 p-30 pt-20 border border-charcoal-100 rounded-20',
            )}
        >
            <Typography fontWeight='medium' variant='h4'>
                Quick Guide
            </Typography>
            {isMob ? (
                <MobServiceList data={data} onClick={onServiceCardClick} />
            ) : (
                <WebServiceList data={data} onClick={onServiceCardClick} />
            )}
            <Dialog
                classes={{ container: 'h-full !rounded-none', root: '!justify-end' }}
                onClose={() => setSelection(null)}
                open={!!selection}
                TransitionComponent={DesktopSlide}
            >
                <div className='flex flex-col w-27.5 h-full'>
                    <DialogHeader onClose={() => setSelection(null)} title={selection?.title} />
                    <div className='grow flex flex-col gap-15 px-30 py-20 overflow-scroll'>
                        <ServiceDetail id={selection?.id} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ServiceList;
